/**
 * [TEXT-TRACE] — action-text flapping tracer.
 *
 * Records every change to an action's *displayed* text (and which path caused it)
 * so a single live call yields an exact, ordered, causal record of what flapped,
 * when, and why. Reducer-driven changes are caught by a Redux middleware; render/
 * throttle/precedence-driven changes are caught by per-card/per-carousel effects;
 * backend emitters log with the same `[TEXT-TRACE]` prefix. All three streams
 * carry `actionId` + `ts` so they merge into one timeline per action.
 *
 * Toggle (any one): URL `?texttrace=1`, localStorage `TEXT_TRACE=1`, or runtime
 * config `NEXT_PUBLIC_TEXT_TRACE=1`. Off by default — zero overhead when off.
 *
 * Read server-side:
 *   python3 scripts/callsim/pod_logs_cli.py --namespace demo \
 *     --deployment neutralaiz-ui-frontend --since 15m | grep '\[TEXT-TRACE\]'
 *   (every pod, with a coverage line on stderr; `logs deploy/<name>` reads only one pod)
 * (the tracer beacons to the existing /api/log endpoint, which Winston persists +
 *  prints to the pod console). Merge with the agent-caller/api-ai-engine streams,
 *  sort by ts, filter to one actionId for the full causal chain. A record too
 *  large for one /api/log body arrives as parts sharing a traceId; join their
 *  payloads in part.index order and JSON.parse (see partBodies).
 */

import {
  LOG_ENDPOINT_MAX_BODY_BYTES,
  TRACE_PART_MAX_ATTEMPTS,
  TRACE_PART_MIN_INTERVAL_MS,
  TRACE_PART_RETRY_FALLBACK_MS,
  TRACE_PENDING_PARTS_KEY,
} from '@/lib/logEndpointLimits';

const isBrowser = typeof window !== 'undefined';

let _enabled: boolean | null = null;

export function textTraceEnabled(): boolean {
  if (_enabled !== null) return _enabled;
  if (!isBrowser) {
    _enabled = process.env.NEXT_PUBLIC_TEXT_TRACE === '1';
    return _enabled;
  }
  try {
    const u = new URLSearchParams(window.location.search).get('texttrace');
    if (u === '1') window.localStorage.setItem('TEXT_TRACE', '1');
    if (u === '0') window.localStorage.removeItem('TEXT_TRACE');
    _enabled =
      window.localStorage.getItem('TEXT_TRACE') === '1' ||
      (window as any).__RUNTIME_CONFIG__?.NEXT_PUBLIC_TEXT_TRACE === '1' ||
      process.env.NEXT_PUBLIC_TEXT_TRACE === '1';
  } catch {
    _enabled = false;
  }
  return _enabled;
}

/** Effective displayed text — the exact precedence the cards/carousel resolve. */
export function effDisplay(a: any): string {
  return a?.improvementPatch?.improvedQuestion ?? a?.highlightedQuestion ?? a?.details ?? '';
}

function beaconBody(rec: Record<string, unknown>): string {
  return (
    '[' +
    JSON.stringify({
      type: 'info',
      component: 'TEXT-TRACE',
      message: '[TEXT-TRACE]',
      data: rec,
    }) +
    ']'
  );
}

const utf8Bytes = (text: string): number => new TextEncoder().encode(text).byteLength;

/**
 * /api/log refuses a body over LOG_ENDPOINT_MAX_BODY_BYTES with 413, which
 * sendBeacon never reports. A record too large for one body is shipped in parts:
 * the whole record is serialized to JSON and that `payload` is cut into pieces,
 * each sent as `{ ts, traceId, part: { index, count }, payload }`. Every part
 * fits whatever the size of any field. To read one back: take the parts with
 * its traceId, join their payloads in index order, and JSON.parse the result.
 */
function* partBodies(rec: Record<string, unknown>): Generator<string> {
  const payload = JSON.stringify(rec);
  const ts = rec.ts;
  const traceId = `${String(ts)}-${Math.random().toString(36).slice(2, 10)}`;
  const envelope = utf8Bytes(beaconBody({ ts, traceId, part: { index: 0, count: 0 }, payload: '' }));
  // Worst-case JSON escaping is 6 bytes per UTF-16 code unit (\uXXXX); the
  // slack covers the part counters' digits.
  const pieceChars = Math.max(1, Math.floor((LOG_ENDPOINT_MAX_BODY_BYTES - envelope - 64) / 6));
  const count = Math.ceil(payload.length / pieceChars);
  for (let index = 0; index < count; index += 1) {
    yield beaconBody({
      ts,
      traceId,
      part: { index: index + 1, count },
      payload: payload.slice(index * pieceChars, (index + 1) * pieceChars),
    });
  }
}

// A record that fits the bound goes by keepalive fetch, so its delivery is
// acknowledged; a refusal or failure is ERROR-logged (identity only, never the
// payload). A beacon is only the fallback after a failed fetch, and its dispatch
// is ERROR-logged as unacknowledged.
function send(body: string, identity: { actionId: unknown }): void {
  if (!isBrowser) return;
  const beaconFallback = (reason: string) => {
    let dispatched = false;
    try {
      dispatched = !!navigator.sendBeacon?.('/api/log', new Blob([body], { type: 'application/json' }));
    } catch {
      dispatched = false;
    }
    console.error(
      dispatched ? '[TEXT-TRACE] trace sent by beacon after a failed request; delivery unacknowledged' : '[TEXT-TRACE] trace lost',
      { ...identity, reason },
    );
  };
  try {
    fetch('/api/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).then((response) => {
      if (!response.ok) console.error('[TEXT-TRACE] trace refused by the endpoint', { ...identity, status: response.status });
    }, (error) => beaconFallback(`request_failed:${errorReason(error)}`));
  } catch (error) {
    beaconFallback(`request_threw:${errorReason(error)}`);
  }
}

// Parts go through one queue, one request at a time, each after the previous
// response:
// - the browser caps the bytes of in-flight beacon/keepalive requests (64 KiB in
//   total), so parts sent together could be refused;
// - /api/log shares one rate-limit bucket (100/min) with every public route, so
//   parts are paced at TRACE_PART_MIN_INTERVAL_MS and a 429 is retried after its
//   Retry-After instead of losing the part;
// - each part is written to localStorage BEFORE the next one is constructed;
//   repeated traces do not retain a growing array of bodies in JS memory.
//   `pagehide` pauses the pump; `pageshow` (including BFCache) resumes it.
// A part that is still not stored is reported, never dropped silently.
type PendingPart = { body: string; index: number; count: number; attempts: number };

const PART_KEY_PREFIX = `${TRACE_PENDING_PARTS_KEY}:part:`;
const NEXT_SEQUENCE_KEY = `${TRACE_PENDING_PARTS_KEY}:next`;
const queueOwner = {};
const queueWindow = isBrowser ? window as Window & { __textTraceQueueOwner?: object } : null;
if (queueWindow) queueWindow.__textTraceQueueOwner = queueOwner;
const ownsQueue = () => queueWindow?.__textTraceQueueOwner === queueOwner;
let pumping = false;
let pageHidden = false;
let lifecycleArmed = false;
let inFlightPart: PendingPart | null = null;

function partIdentity(part: PendingPart): { traceId: string | undefined; index: number; count: number } {
  // The payload is intentionally excluded from operational loss logs.
  let traceId: string | undefined;
  try { traceId = JSON.parse(part.body)[0]?.data?.traceId; } catch { /* legacy malformed part */ }
  return { traceId, index: part.index, count: part.count };
}

/**
 * The page's localStorage, or null where the document may not use it (sandboxed or opaque-origin frames,
 * storage-blocked browsers). Reading `window.localStorage` itself throws SecurityError there; a trace
 * helper must never break the page, so every queue access goes through this.
 */
function traceStorage(): Storage | null {
  if (!isBrowser) return null;
  try { return window.localStorage; } catch { return null; }
}

function errorReason(error: unknown): string {
  return error instanceof Error ? error.name : typeof error;
}

/**
 * Take one key snapshot per scan. Storage.length/key(i) can each rebuild the
 * whole key list (including Happy DOM 15), making an indexed scan quadratic
 * and draining a long trace cubic. Keep keys only, never queued payload bodies;
 * each later scan is fresh so another tab's additions remain visible.
 */
function storedPartKeys(store: Storage): string[] {
  return Object.keys(store).filter((key) => key.startsWith(PART_KEY_PREFIX));
}

function persistedPartsFor(traceId: string | undefined): number {
  const store = traceStorage();
  if (!store) return 0;
  let count = 0;
  for (const key of storedPartKeys(store)) {
    const raw = store.getItem(key);
    if (!raw) continue;
    try {
      if (partIdentity(JSON.parse(raw) as PendingPart).traceId === traceId) count += 1;
    } catch { /* A corrupt saved part is unrelated to this trace. */ }
  }
  return count;
}

function nextPartKey(store: Storage): string {
  const next = Number(store.getItem(NEXT_SEQUENCE_KEY) || '0') + 1;
  store.setItem(NEXT_SEQUENCE_KEY, String(next));
  // The suffix prevents two tabs that read the same sequence from overwriting
  // each other's part body. Sequence still gives the drain order.
  return `${PART_KEY_PREFIX}${next}-${Math.random().toString(36).slice(2)}`;
}

function persistPart(part: PendingPart): void {
  const store = traceStorage();
  if (!store) throw new DOMException('localStorage is not available to this document', 'SecurityError');
  store.setItem(nextPartKey(store), JSON.stringify(part));
}

function oldestPart(): { key: string; part: PendingPart } | null {
  const store = traceStorage();
  if (!store) return null;
  let oldest = Number.POSITIVE_INFINITY;
  let key: string | null = null;
  for (const candidate of storedPartKeys(store)) {
    const sequence = Number.parseInt(candidate.slice(PART_KEY_PREFIX.length), 10);
    if (sequence < oldest) { oldest = sequence; key = candidate; }
  }
  if (!key) return null;
  const raw = store.getItem(key);
  return raw ? { key, part: JSON.parse(raw) as PendingPart } : null;
}

export function getTextTraceQueueStatus(): { inMemoryParts: number; durableParts: number } {
  const store = traceStorage();
  if (!store) return { inMemoryParts: pumping ? 1 : 0, durableParts: 0 };
  return { inMemoryParts: pumping ? 1 : 0, durableParts: storedPartKeys(store).length };
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function retryAfterMs(response: Response): number {
  const seconds = Number(response.headers.get('Retry-After'));
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : TRACE_PART_RETRY_FALLBACK_MS;
}

/** Posts one part. Returns the wait before retrying it, or null when it is finished with. */
async function postPart(part: PendingPart): Promise<number | null> {
  const where = partIdentity(part);
  part.attempts += 1;
  try {
    const response = await fetch('/api/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: part.body,
      keepalive: true,
    });
    if (response.ok) return null;
    if (response.status === 429 && part.attempts < TRACE_PART_MAX_ATTEMPTS) return retryAfterMs(response);
    console.error('[TEXT-TRACE] trace part lost', { ...where, status: response.status, attempts: part.attempts, reason: 'http_rejected' });
  } catch {
    console.error('[TEXT-TRACE] trace part lost', { ...where, attempts: part.attempts, reason: 'network_error' });
  }
  return null;
}

async function pumpParts(): Promise<void> {
  if (pumping || pageHidden || !ownsQueue()) return;
  pumping = true;
  try {
    while (!pageHidden && ownsQueue()) {
      const pending = oldestPart();
      if (!pending) break;
      // Taken off the queue while in flight: a keepalive request outlives the page,
      // so `pagehide` saves only the parts not yet started.
      const { key, part } = pending;
      inFlightPart = part;
      traceStorage()?.removeItem(key);
      const retryMs = await postPart(part);
      inFlightPart = null;
      if (retryMs !== null) {
        try {
          const store = traceStorage();
          if (!store) throw new DOMException('localStorage is not available to this document', 'SecurityError');
          store.setItem(key, JSON.stringify(part));
        }
        catch (error) {
          console.error('[TEXT-TRACE] trace part lost', { ...partIdentity(part), attempts: part.attempts, reason: 'retry_persistence_failed', errorName: errorReason(error) });
        }
      }
      if (!pageHidden && ownsQueue() && oldestPart()) await sleep(retryMs ?? TRACE_PART_MIN_INTERVAL_MS);
    }
  } finally {
    pumping = false;
    if (!pageHidden && ownsQueue() && oldestPart()) void pumpParts();
  }
}

function armLifecycle(): void {
  if (lifecycleArmed) return;
  lifecycleArmed = true;
  window.addEventListener('pagehide', () => {
    if (!ownsQueue()) return;
    if (!pageHidden && inFlightPart) {
      console.error('[TEXT-TRACE] trace part unacknowledged at pagehide', { ...partIdentity(inFlightPart), reason: 'in_flight_removed_from_storage' });
    }
    pageHidden = true;
  });
  window.addEventListener('pageshow', () => {
    if (!ownsQueue()) return;
    pageHidden = false;
    resumeSavedParts();
    void pumpParts();
  });
}

function enqueueParts(parts: Iterable<PendingPart>): void {
  if (!isBrowser) return;
  armLifecycle();
  for (const part of parts) {
    try {
      persistPart(part);
    } catch (error) {
      const identity = partIdentity(part);
      console.error('[TEXT-TRACE] trace persistence failed', { ...identity, persistedParts: persistedPartsFor(identity.traceId), reason: errorReason(error) });
      throw error;
    }
  }
  void pumpParts();
}

function* pendingPartsFor(bodies: Iterable<string>): Generator<PendingPart> {
  for (const body of bodies) {
    const { index, count } = JSON.parse(body)[0].data.part as { index: number; count: number };
    yield { body, index, count, attempts: 0 };
  }
}

function sendInOrder(bodies: Iterable<string>): void {
  enqueueParts(pendingPartsFor(bodies));
}

/** Migrate the previous pagehide queue. The new queue is already durable part by part. */
function resumeSavedParts(): void {
  if (!isBrowser) return;
  let saved: PendingPart[] = [];
  try {
    const raw = window.localStorage.getItem(TRACE_PENDING_PARTS_KEY);
    if (!raw) return;
    saved = JSON.parse(raw) as PendingPart[];
    for (const part of saved) persistPart({ ...part, attempts: 0 });
    window.localStorage.removeItem(TRACE_PENDING_PARTS_KEY);
  } catch {
    console.warn('[TEXT-TRACE] saved trace parts could not be read');
    return;
  }
  armLifecycle();
  void pumpParts();
}

if (isBrowser) armLifecycle();
resumeSavedParts();
if (isBrowser) void pumpParts();

// Dedicated sender — bypasses lib/logger's 10/s rate limiter so flap BURSTS are
// never dropped. Lands in the /api/log 'info' branch (Winston file + console).
function ship(rec: Record<string, unknown>): void {
  try {
    const whole = beaconBody(rec);
    if (utf8Bytes(whole) <= LOG_ENDPOINT_MAX_BODY_BYTES) send(whole, { actionId: rec.actionId });
    else sendInOrder(partBodies(rec));
    if (isBrowser) console.debug('[TEXT-TRACE]', rec);
  } catch (error) {
    if (isBrowser) console.error('[TEXT-TRACE] trace could not be queued', { actionId: rec.actionId, reason: errorReason(error) });
  }
}

export function trace(rec: {
  site: string;
  actionId: string;
  oldText?: string;
  newText?: string;
  changedField?: string;
  cause?: string;
  extra?: Record<string, unknown>;
}): void {
  if (!textTraceEnabled()) return;
  ship({
    ts: Date.now(),
    perf: isBrowser ? Math.round(performance.now()) : 0,
    // The whole text: a flap trace exists to show exactly what changed.
    ...rec,
  });
}
